import React, { Component } from 'react';
import { BBPDFViewerUI, defaultToolbar, annotationLibraryItem, preloadInstance } from '@bb-remark/pdf-viewer-ui';
import { getParamsFromUrl, ticketExchange } from './utils/urlParams';
import { JssThemeProvider, SnackbarProvider } from '@bb-ui/react-library/dist/components';
import { Comment } from '@bb-ui/icons/dist/medium/Comment';
import { LibrarySidebar, MenuItemType } from '@bb-remark/annotation-library';
import { Banner } from '@bb-ui/react-library/dist/components/Banner';
import { getCustomString, getDecodedJwt, setDecodedJwt } from '@bb-remark/web-utils';
import { ConversionScreen, CONVERSION_STATUS, LocalePicker, NotificationModal, MaintenanceScreen, MAINTENANCE_STATUS, SpinnerScreen } from './components';
import { usePersistentAnnotationBehavior } from './behaviors/persistentAnnotation';
import {
  fetchUserPreferences, setUserPreferences,
  fetchUserStamps, saveUserStamps,
  getNonDefaultPresets, stampAnnotationToJson,
  textToObject, getStringFromObject,
} from './utils/userPreferences';

class App extends Component {
  constructor(props) {
    super(props);
    const { pdfKitUrl, ticket, locale, maintenance, status, standalone } = getParamsFromUrl();
    let isLocalDevelopment = /pr-\d+|localhost/g.test(window.location.hostname);

    if ( !isLocalDevelopment ) {
      //Hide the url for Stage/Prod
      window.history.replaceState(
        { pdfviewer: 'pdfviewer' },
        '',
        'index.html'
      );
    }

    const toggleLibraryOpen = async () => {
      this.setState(state => ({
        isLibraryOpen: !state.isLibraryOpen,
        menuItems: [{
          title: getCustomString('placeComment'),
          icon: <Comment/>,
          type: MenuItemType.CUSTOM,
          onClick: this.placeAnnotation.bind(this)
        }, {
          type: MenuItemType.COPY
        },{
          type: MenuItemType.EDIT
        }, {
          type: MenuItemType.DELETE
        }]
      }));
      this.state.instance.setToolbarItems(createToolbar());
    };

    const createToolbar = () => {
      const toolbar = [...defaultToolbar()];
      const jwt = getDecodedJwt();
      if (jwt.permissions && !jwt.permissions.includes('write')) {
        const index = toolbar.findIndex(obj => obj.type === 'responsive-group');
        toolbar[index].disabled = true;
      }
      if (jwt.permissions && jwt.permissions.includes('write') && jwt.features && jwt.features.includes('annotationLibrary')) {
        const libraryItem = annotationLibraryItem(toggleLibraryOpen, this.state.isLibraryOpen);
        toolbar.push(libraryItem);
      }
      return toolbar;
    };

    const onLoad = (instance) => {
      instance.timer;
      // workaround from pspsdfkit for https://jira.bbpd.io/browse/LRN-162302 / https://support.pspdfkit.com/hc/en-us/requests/18358
      instance.contentDocument.addEventListener(
        'click',
        e => {
          const annotationDetails = e.target.closest('.PSPDFKit-Sidebar-Annotations-Annotation[data-annotation-id]');
          if (annotationDetails) {
            const annotationElement = instance.contentDocument.querySelector(
              `.PSPDFKit-Annotation[data-annotation-id="${annotationDetails.getAttribute('data-annotation-id')}"]`
            );
            if (annotationElement) {
              annotationElement.setAttribute('data-focusvisible-polyfill', true);
              annotationElement.focus();
            }
          }
        },
        { capture: true }
      );

      instance.addEventListener('annotationPresets.update', async (e) => {
        // Disable multiple endpoint calls while using an slider to set a preference
        if (e.newPresetProperties?.opacity || e.newPresetProperties?.lineWidth || e.newPresetProperties?.strokeWidth) {
          clearTimeout(instance.timer);
          instance.timer = setTimeout(async () => {
            await setAnnotationPresets(e);
          }, 500);
          return;
        }
        await setAnnotationPresets(e);
      });

      instance.addEventListener('annotations.create', async (e) => {
        const annotation = e.get(0);
        if (!(annotation instanceof PSPDFKit.Annotations.StampAnnotation)) {
          return;
        }

        if (annotation.stampType !== 'Custom') {
          return;
        }

        const existingStamp = instance.stampAnnotationTemplates.some((stamp) => stamp.title === annotation.title &&
          stamp.subtitle === annotation.subtitle && stamp.color === annotation.color);
        if (existingStamp) {
          return;
        }

        instance.setStampAnnotationTemplates(stampAnnotationTemplates => {
          return [annotation, ...stampAnnotationTemplates];
        });
        const plainAnnotation = stampAnnotationToJson(annotation);
        const plainAnnotationStr = getStringFromObject('stamp', plainAnnotation);
        try {
          await saveUserStamps(plainAnnotationStr, this.state.config.authPayload.jwt);
        } catch (err) {
          // we want to avoid breaking the UI if it fails.
          console.error(err);
        }
      });

      usePersistentAnnotationBehavior(instance);
      this.setState({ instance });
    };

    const setAnnotationPresets = async (e) => {
      try {
        let currentUpdatedPreferences = this.state.currentUpdatedPreferences;
        if (!currentUpdatedPreferences.version) {
          currentUpdatedPreferences.version = '1';
        }

        currentUpdatedPreferences[e.currentPreset] = {
          ...currentUpdatedPreferences[e.currentPreset],
          ...e.newPresetProperties,
        };
        currentUpdatedPreferences = getNonDefaultPresets(currentUpdatedPreferences, e.currentPreset, e.newPresetProperties);

        const currentUpdatedPreferencesStr = getStringFromObject('preferences', currentUpdatedPreferences);
        await setUserPreferences(currentUpdatedPreferencesStr, this.state.config.authPayload.jwt);
      } catch (err) {
        // we want to avoid breaking the UI if it fails.
        console.error(err);
      }
    };

    const getAnnotationPresets = async (defaults) => {
      try {
        let currentPreferences = await fetchUserPreferences(this.state.config.authPayload.jwt);

        let currentUpdatedPreferences = {};
        Object.keys(currentPreferences ?? {}).forEach(key => {
          const mergablePreferences = Object.keys(currentPreferences[key]).reduce((acc, k) => ({
            ...acc,
            [k]: textToObject(currentPreferences[key][k]),
          }), {});

          defaults[key] = {
            ...defaults[key],
            ...mergablePreferences,
          };
          currentUpdatedPreferences[key] = {
            ...currentUpdatedPreferences[key],
            ...currentPreferences[key],
          };
        });
        this.setState({ currentUpdatedPreferences });
      } catch (err) {
        // we want to avoid breaking the UI if it fails.
        console.error(err);
      }
      return defaults;
    };

    const getStampAnnotationTemplates = async (defaults) => {
      try {
        let loadedStamps = await fetchUserStamps(this.state.config.authPayload.jwt);

        const userStamps = loadedStamps.map((stamp) => {
          return new PSPDFKit.Annotations.StampAnnotation({
            stampType: stamp.stampType,
            title: stamp.title,
            subtitle: stamp.subtitle,
            color: new PSPDFKit.Color(stamp.color),
            boundingBox: new PSPDFKit.Geometry.Rect(stamp.bbox)
          });
        });
        return [...userStamps, ...defaults];
      } catch (err) {
        // we want to avoid breaking the UI if it fails.
        console.error(err);
      }
      return defaults;
    };

    this.state = {
      config: {
        getAnnotationPresets,
        getStampAnnotationTemplates,
        authPayload: null,
        baseUrl: pdfKitUrl + '/',
        locale,
        onLoad,
        serverUrl: pdfKitUrl + '/',
        cssOverridesPath: 'override.css',
        toolbarItems: createToolbar,
        // Disable long polling / sync. Syncs will happen when changes occur
        listenToServerChangesEnabled: false,
        standalone
      },
      currentUpdatedPreferences: {},
      decodedJwt: null,
      documentStatus: status,
      isLibraryOpen: false,
      isLocalDevelopment,
      instance: null,
      maintenance,
      modalProps: null,
      showModal: false,
      showBanner: true,
      selectedText: null,
      sessionExpired: false,
      ticket,
    };

    this.handleResize = this.handleResize.bind(this);
    this.closeLibrary = this.closeLibrary.bind(this);
    this.createComment = this.createComment.bind(this);
    this.componentsToRender = this.componentsToRender.bind(this);
    this.toggleModal = this.toggleModal.bind(this);
  }

  async doTicketExchange() {
    let serverTime;
    try {
      //Response from the tickets endpoint
      const response = await ticketExchange(this.state.ticket);
      //Get the exact time the request was made to the service. Get the JWT and set it internally
      serverTime = new Date(response.headers.get('date'));
      const body = await response.json();
      setDecodedJwt(body.jwt);

      this.setState( state => {
        state.config.authPayload = { jwt: body.jwt };
        state.decodedJwt = getDecodedJwt();
        return state;
      });
    } catch (error) {
      this.setState( state => {
        state.config.authPayload = { jwt: error };
        state.documentStatus = CONVERSION_STATUS.ERROR;
        return state;
      });
    }

    return serverTime;
  }

  async componentDidMount() {
    window.addEventListener('resize', this.handleResize);
    let serverTime;
    if (this.state.maintenance !== MAINTENANCE_STATUS.TRUE) {
      // Preload pspdfkit resources - We do not need to await on this and it can occur in parallel
      preloadInstance(this.state.config.baseUrl, this.state.config.standalone);
      serverTime = await this.doTicketExchange();
    } else {
      this.setState(state => {
        state.config.authPayload = { };
        state.documentStatus = CONVERSION_STATUS.ERROR;
        return state;
      });
    }

    if( this.state.decodedJwt ) {
      //Get time until session expires. Comes in seconds since Epoch format see https://pspdfkit.com/guides/server/current/pspdfkit-server/client-authentication/
      let timeToExpire = new Date(this.state.decodedJwt.exp * 1000).getTime() - serverTime.getTime();
      const sessionExpires = new Date().getTime() + timeToExpire;
      let modalShown = false;

      //Check every 15 seconds to see how close we are to session expiration
      //This works around a thread sleeping issue in chrome with setTimeout
      let sessionInterval = setInterval(() => {
        const now = new Date().getTime();

        //If we're within 6 minutes and the modal hasn't been shown, show modal
        if ( now > sessionExpires - 360000 && !modalShown) {
          //Show warnings when maintenance window incoming
          if ( this.state.maintenance === MAINTENANCE_STATUS.PENDING ) {
            this.toggleModal({
              title: getCustomString('maintenanceModalTitle'),
              body: getCustomString('maintenanceModalDescription'),
              confirm: getCustomString('alertModalConfirm'),
              id: 'maintenance'
            });
            modalShown = true;
          }
          else {
            //show the default session expiring modal
            this.toggleModal({
              title: getCustomString('sessionModalTitle'),
              body: getCustomString('sessionModalDescription'),
              confirm: getCustomString('alertModalConfirm'),
              id: 'sessionExpiring'
            });
            modalShown = true;
          }
        }
        //If we're withing 1 minute, cut to expired screen
        if (now > sessionExpires - 60000) {
          if (this.state.maintenance === MAINTENANCE_STATUS.PENDING) {
            this.setState( state => {
              state.maintenance = MAINTENANCE_STATUS.TRUE;
              return state;
            });
          } else {
            this.setState( state => {
              state.sessionExpired = true;
              return state;
            });
          }
          clearInterval(sessionInterval);
        }
      }, 15000);
    }
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.handleResize);
  }

  async handleResize() {
    if (this.state.isLibraryOpen) {
      await this.setState({ isLibraryOpen: window.innerWidth > 640 });
      this.state.instance.setToolbarItems(this.state.config.toolbarItems());
    }
  }

  async closeLibrary() {
    await this.setState({ isLibraryOpen: false });
    this.state.instance.setToolbarItems(this.state.config.toolbarItems());
  }

  closeBanner() {
    this.setState({ showBanner: false });
  }

  async createComment() {
    const { selectedText, instance } = this.state;
    if (!selectedText || !instance) {
      return;
    }

    const selectedAnnotation = instance.getSelectedAnnotation();
    if (!(selectedAnnotation instanceof PSPDFKit.Annotations.CommentMarkerAnnotation)) {
      return;
    }

    const observer = new MutationObserver(function () {
      const element = instance.contentDocument.querySelector(
        '.PSPDFKit-Comment-Thread-Wrapper-Selected .PSPDFKit-Comment-Thread textarea'
      );
      if (element) {
        observer.disconnect();
        const event = new Event('input', { bubbles: true });
        const nativeInputValueSetter = Object.getOwnPropertyDescriptor(window.HTMLTextAreaElement.prototype, 'value').set;

        nativeInputValueSetter.call(element, selectedText);
        element.dispatchEvent(event);
      }
    });
    observer.observe(instance.contentDocument, {
      childList: true,
      subtree: true
    });

    // cleanup
    this.setState({ selectedText : null });
    instance.removeEventListener('annotations.change', this.createComment);
  }

  placeAnnotation(selectedText) {
    const { instance } = this.state;
    instance.removeEventListener('annotations.change', this.createComment);
    instance.setViewState(viewState => viewState.set('interactionMode', null));
    this.setState({ selectedText }, () => {
      instance.addEventListener('annotations.change', this.createComment);
      instance.setViewState(viewState => viewState.set('interactionMode', PSPDFKit.InteractionMode.COMMENT_MARKER));
    });
  }

  onLocaleSelection(option) {
    const config = this.state.config;
    config.locale = option.value;
    this.setState(config);
  }

  // A completed document status after conversion spinner will include if we can use standalone mode
  // Completed documents can provide this hint in the initial get params
  updateStatus(documentStatus, standalone) {
    this.setState(state => {
      return {
        config: {
          ...state.config,
          standalone,
        },
        documentStatus,
      };
    });
  }

  toggleModal( modalProps ) {
    this.setState(state => {
      state.showModal = !state.showModal;
      state.modalProps = modalProps;
      return state;
    });
  }

  componentsToRender() {
    if (!this.state.config.authPayload) {
      return (
        <SpinnerScreen />
      );
    }

    if (this.state.maintenance === MAINTENANCE_STATUS.TRUE) {
      //Display the Maintenance Screen to prevent User Interaction while back-end is down
      return <MaintenanceScreen />;
    }

    if (this.state.sessionExpired) {
      return <SpinnerScreen title={getCustomString('sessionExpiredTitle')} description={getCustomString('sessionExpiredDesc')} error={true} />;
    }

    if (this.state.documentStatus !== CONVERSION_STATUS.COMPLETE) {
      //Display conversion screen and come back once document is converted
      return <ConversionScreen config={this.state.config} status={this.state.documentStatus} updateStatus={this.updateStatus.bind(this)}/>;
    }

    if (!this.state.config.locale && this.state.isLocalDevelopment) {
      //Display Localization Picker for Translators
      return <LocalePicker onLocaleSelection={this.onLocaleSelection.bind(this)}/>;
    }

    const CONVERSION_WARNING_TYPES = ['docx', 'doc', 'pptx', 'ppt'];
    const jwt = getDecodedJwt();
    let hasConversionWarning;
    if (jwt.orig_file_type) {
      const origFileType = jwt.orig_file_type;
      hasConversionWarning = CONVERSION_WARNING_TYPES.includes(origFileType) && jwt.permissions && jwt.permissions.includes('write');
    }

    //Display BB-Annotate
    return (
      <div className="snackbar-container" >
        <SnackbarProvider
          //This uses react portal to render the alert outside of the aria-hidden context it's called from, guaranteeing narrator will always read it.
          domRoot={document.getElementById('root')}
        >
          { (this.state.showBanner && hasConversionWarning) ?
            <Banner className="banner" severity="info" onClose={() => {this.closeBanner();}} closeText={ getCustomString('dismiss') }>
              { getCustomString('conversionDiscrepancy') }
            </Banner> : null }
          <div className={this.state.showBanner && hasConversionWarning ? 'UIcontainerWithBanner' : 'UIcontainerWithoutBanner'}>
            <BBPDFViewerUI
              configuration = {this.state.config}
            />
            { (this.state.isLibraryOpen) ?
              <LibrarySidebar
                closeLibrary={this.closeLibrary}
                menuItems={this.state.menuItems}
              /> : null }
            <NotificationModal
              modalVisible={ this.state.showModal }
              toggleModal={ this.toggleModal.bind(this) }
              modalProps={ this.state.modalProps }
            />
          </div>
        </SnackbarProvider>
      </div>
    );

  }

  render() {
    return (
      <JssThemeProvider theme='light'>
        {this.componentsToRender()}
      </JssThemeProvider>
    );
  }
}

export default App;
